import { useState, useRef, useEffect } from 'react';

const TYPE_META = {
  filename:   { label: 'Filename',   color: '#2563eb', icon: '📄' },
  category:   { label: 'Category',   color: '#7c3aed', icon: '📂' },
  narrative:  { label: 'Narrative',  color: '#d97706', icon: '📝' },
  identifier: { label: 'Identifier', color: '#0891b2', icon: '🔖' },
  unknown:    { label: 'Unknown',    color: '#6b7280', icon: '❓' },
};

const ALL_TYPES = ['filename', 'category', 'narrative', 'identifier', 'unknown'];

/**
 * ItemTypeBadge — small colored chip showing a checklist item's classified type.
 *
 * If onOverride is provided, clicking the badge opens an inline picker
 * letting the reviewer change the type. Otherwise the badge is a static
 * label.
 *
 * Props:
 *   type            — one of filename | category | narrative | identifier | unknown
 *   needsReview     — boolean: render with extra emphasis if true
 *   reviewerOverrode — boolean: render with a "user-set" indicator if true
 *   subtitle        — optional second line (canonical category, narrative subject)
 *   onOverride      — async (newType) => void; enables click-to-override
 *   disabled        — disable the picker while a request is in flight
 */
export default function ItemTypeBadge({
  type,
  needsReview = false,
  reviewerOverrode = false,
  subtitle = null,
  onOverride = null,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const meta = TYPE_META[type] || TYPE_META.unknown;
  const clickable = !!onOverride && !disabled;

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  async function pick(newType) {
    if (newType === type) { setOpen(false); return; }
    setBusy(true);
    try {
      await onOverride(newType);
    } finally {
      setBusy(false);
      setOpen(false);
    }
  }

  const baseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 8px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    border: `1px solid ${meta.color}`,
    background: needsReview ? meta.color : 'transparent',
    color: needsReview ? '#fff' : meta.color,
    cursor: clickable ? 'pointer' : 'default',
    opacity: busy ? 0.5 : 1,
    whiteSpace: 'nowrap',
  };

  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <span
        style={baseStyle}
        onClick={clickable ? () => setOpen(o => !o) : undefined}
        title={clickable ? 'Click to change item type' : meta.label}
      >
        {meta.icon} {meta.label}
        {reviewerOverrode && <span title="Reviewer set" style={{ marginLeft: 2 }}>✓</span>}
        {needsReview && <span title="Needs review" style={{ marginLeft: 2 }}>!</span>}
      </span>
      {subtitle && (
        <span style={{ marginLeft: 6, fontSize: 11, color: '#6b7280' }}>{subtitle}</span>
      )}
      {open && (
        <div
          style={{
            position: 'absolute', top: '110%', left: 0, zIndex: 100,
            background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)', padding: 4, minWidth: 140,
          }}
        >
          {ALL_TYPES.map(t => {
            const m = TYPE_META[t];
            const active = t === type;
            return (
              <button
                key={t}
                onClick={() => pick(t)}
                disabled={busy}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  width: '100%', padding: '5px 8px',
                  background: active ? '#f3f4f6' : 'transparent',
                  border: 'none', cursor: 'pointer', borderRadius: 4,
                  fontSize: 12, color: m.color, fontWeight: active ? 600 : 500,
                  textAlign: 'left',
                }}
              >
                {m.icon} {m.label}
                {active && <span style={{ marginLeft: 'auto', color: '#6b7280' }}>•</span>}
              </button>
            );
          })}
        </div>
      )}
    </span>
  );
}
